// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Small shared builders. Everything that draws a data mark here also attaches a `data-tip`,
// because a mark without an exact value on hover is a defect in this fleet.

import { GLOSSARY } from '../glossary';
import { escapeHtml } from '../format';

export const SVG_NS = 'http://www.w3.org/2000/svg';

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  html?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (html !== undefined) node.innerHTML = html;
  return node;
}

export function svg<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {}
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, String(value));
  return node;
}

export interface PanelOptions {
  title: string;
  subtitle?: string;
  /** Rendered under the body in smaller type — caveats, method notes, attribution. */
  note?: string;
  className?: string;
}

export function panel(options: PanelOptions): { root: HTMLElement; body: HTMLElement } {
  const root = el('section', `panel${options.className ? ` ${options.className}` : ''}`);
  const head = el('div', 'panel-head');
  head.append(el('h2', 'panel-title', escapeHtml(options.title)));
  if (options.subtitle) head.append(el('p', 'panel-sub', options.subtitle));
  root.append(head);
  const body = el('div', 'panel-body');
  root.append(body);
  if (options.note) {
    const note = el('div', 'panel-body');
    note.append(el('p', 'note', options.note));
    root.append(note);
  }
  return { root, body };
}

export function stat(value: string, label: string, note?: string, tipText?: string): HTMLElement {
  const node = el('div', 'stat');
  if (tipText) node.setAttribute('data-tip', tipText);
  node.append(el('span', 'stat-value', escapeHtml(value)));
  node.append(el('span', 'stat-label', label));
  if (note) node.append(el('span', 'stat-note', escapeHtml(note)));
  return node;
}

export function statRow(...stats: HTMLElement[]): HTMLElement {
  const row = el('div', 'stat-row');
  row.append(...stats);
  return row;
}

/** A legend swatch row. Every swatch is clickable where the view supports filtering. */
export function legend(items: Array<{ label: string; hex: string; tip?: string }>): HTMLElement {
  const root = el('div', 'legend');
  for (const item of items) {
    const chip = el('span', 'legend-item');
    if (item.tip) chip.setAttribute('data-tip', item.tip);
    const swatch = el('span', 'legend-swatch');
    swatch.style.background = item.hex;
    chip.append(swatch, document.createTextNode(item.label));
    root.append(chip);
  }
  return root;
}

export function emptyState(message: string, hint?: string): HTMLElement {
  const node = el('div', 'empty-state');
  node.append(el('p', 'empty-title', escapeHtml(message)));
  if (hint) node.append(el('p', 'empty-hint', hint));
  return node;
}

export function errorState(message: string, onRetry?: () => void): HTMLElement {
  const node = el('div', 'empty-state');
  node.append(el('p', 'empty-title inline-error', escapeHtml(message)));
  node.append(
    el(
      'p',
      'empty-hint',
      'The data files are served from this site, so this usually means a network hiccup rather than a missing dataset.'
    )
  );
  if (onRetry) {
    const button = el('button', 'button');
    button.type = 'button';
    button.textContent = 'Try again';
    button.addEventListener('click', onRetry);
    node.append(button);
  }
  return node;
}

/** Wide-by-nature content scrolls inside its own container so the page body never does. */
export function xScroll(child: Node): HTMLElement {
  const wrap = el('div', 'x-scroll');
  wrap.append(child);
  return wrap;
}

export function select(
  label: string,
  options: Array<{ value: string; label: string }>,
  value: string,
  onChange: (value: string) => void,
  className = ''
): HTMLElement {
  const field = el('label', `select-field ${className}`.trim());
  field.append(el('span', 'field-label', escapeHtml(label)));
  const node = el('select');
  node.setAttribute('aria-label', label);
  for (const option of options) {
    const opt = document.createElement('option');
    opt.value = option.value;
    opt.textContent = option.label;
    if (option.value === value) opt.selected = true;
    node.append(opt);
  }
  node.addEventListener('change', () => onChange(node.value));
  field.append(node);
  return field;
}

/**
 * A segmented control that keeps its own selected state in sync. Setting the classes only at
 * build time leaves the control showing the wrong segment after every click — the view updates
 * underneath it and the control silently lies about what it is showing.
 */
export function segmented(
  options: Array<{ value: string; label: string; tip?: string }>,
  value: string,
  onChange: (value: string) => void
): HTMLElement {
  const root = el('div', 'segmented');
  root.setAttribute('role', 'tablist');
  const buttons: HTMLButtonElement[] = [];
  const sync = (selected: string) => {
    buttons.forEach((button, index) => {
      const on = options[index].value === selected;
      button.classList.toggle('segment-on', on);
      button.setAttribute('aria-selected', String(on));
    });
  };
  for (const option of options) {
    const button = el('button', 'segment');
    button.type = 'button';
    button.textContent = option.label;
    button.setAttribute('role', 'tab');
    if (option.tip) button.setAttribute('data-tip', option.tip);
    button.addEventListener('click', () => {
      sync(option.value);
      onChange(option.value);
    });
    buttons.push(button);
    root.append(button);
  }
  sync(value);
  return root;
}

// ── Glossary popover ────────────────────────────────────────────────────────
// One delegated handler for every `.glossary-link` on the page, present or future. Dismissed by
// clicking away or pressing Escape — the same contract as every other overlay here.

let popover: HTMLElement | null = null;

export function initGlossary(): void {
  const close = () => {
    popover?.remove();
    popover = null;
  };

  document.addEventListener('click', (event) => {
    const trigger = (event.target as Element)?.closest?.('.glossary-link') as HTMLElement | null;
    if (!trigger) {
      if (popover && !(event.target as Element)?.closest?.('.glossary-popover')) close();
      return;
    }
    event.preventDefault();
    const entry = GLOSSARY[trigger.dataset.term ?? ''];
    if (!entry) return;
    close();
    popover = el('div', 'glossary-popover');
    popover.setAttribute('role', 'dialog');
    popover.setAttribute('aria-label', entry.term);
    popover.innerHTML = `<h3>${escapeHtml(entry.term)}</h3><p class="glossary-short">${escapeHtml(
      entry.short
    )}</p><p>${escapeHtml(entry.body)}</p>`;
    document.body.append(popover);
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 24);
    popover.style.width = `${width}px`;
    const left = Math.min(Math.max(12, rect.left), window.innerWidth - width - 12);
    popover.style.left = `${left}px`;
    const height = popover.getBoundingClientRect().height;
    const below = rect.bottom + 8;
    popover.style.top = `${below + height > window.innerHeight ? Math.max(12, rect.top - height - 8) : below}px`;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });

  window.addEventListener('scroll', close, { passive: true });
}
